import React from 'react';
import { Image } from '../Image/Image';
import { NavFooterItem } from './MegaMenu';
import './NavFooter.scss';

/**
 * NavFooter - Horizontal row of links at the bottom of a dropdown panel
 *
 * Renders utility links (e.g. "Contact us", "Documentation") that appear
 * at the bottom of all desktop dropdown panels. Supports SWB-style items
 * with optional icon + text + link pattern.
 *
 * Features:
 * - Horizontal link row with separator styling
 * - Optional icon per item (SWB-style)
 * - Optional plain text before link (SWB-style)
 * - Analytics tracking via data-cta-id
 * - Configurable link target (defaults to _blank)
 */

export interface NavFooterProps {
  /** Array of footer link items */
  items: NavFooterItem[];
  /** `desktop` (default): one row with separators under a dropdown panel. `mobile`: stacked links in the drawer. */
  variant?: 'desktop' | 'mobile';
}

export const NavFooter: React.FC<NavFooterProps> = ({ items, variant = 'desktop' }) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <footer className={`poly-nav-footer${variant === 'mobile' ? ' poly-nav-footer--mobile' : ''}`}>
      <ul className="poly-nav-footer__list" role="list">
        {items.map((item, index) => (
          <li key={item.id} className="poly-nav-footer__item">
            {index > 0 && variant === 'desktop' && (
              <span className="poly-nav-footer__separator" aria-hidden="true" />
            )}
            {item.iconUrl && (
              <span className="poly-nav-footer__icon">
                <Image bare url={item.iconUrl} alt="" width={16} height={16} aria-hidden="true" />
              </span>
            )}
            {item.text && (
              <span className="poly-nav-footer__text">{item.text}</span>
            )}
            <a
              href={item.url}
              className="poly-nav-footer__link"
              target={item.target ?? '_blank'}
              rel={item.target === '_self' ? undefined : 'noopener noreferrer'}
              data-cta-id={item.id}
            >
              {item.title}
            </a>
          </li>
        ))}
      </ul>
    </footer>
  );
};
